import axios, { AxiosResponse } from 'axios';

import { actions } from '../store/authentication/AuthSlice';
import store from '../store/store';
import generalSlice from '../store/general/GeneralSlice';
import { ErrorToast, SuccessToast } from '../utils/toasters';
import { BASE_URL } from '../utils/Endpoints';

const defaultOptions = {
    headers: {
        'Content-Type': 'application/json',
        accept: 'text/html',
    },
};

export const instance = axios.create(defaultOptions);

instance.interceptors.request.use(function (config) {
    const token = localStorage.getItem('token') ?? '';
    config.headers.Authorization = token ? `Bearer ${token}` : '';
    const appVersion = localStorage.getItem('appVersion');
    config.headers['app-version'] = appVersion ?? '';
    return config;
});

instance.interceptors.response.use(
    response => {
        return response;
    },
    async error => {
        const originalRequest = error.config;
        if (error.response.status === 505) {
            store.dispatch(generalSlice.actions.setVersionModal(true));
            localStorage.setItem('appVersion', error.response.data?.appVersion ?? '');
        }
        if (error.response.status === 403) {
            ErrorToast(error.response.data);
            // history.push('/403');
            window.location.href = '/403';
        }
        if (error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem('refreshToken');

            if (refreshToken) {
                try {
                    const response = await axios.post(`${BASE_URL}auth/refresh`, { refreshToken });
                    // don't use axious instance that already configured for refresh token api call
                    const newAccessToken = response?.data.token;
                    localStorage.setItem('token', newAccessToken); //set new access token
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return axios(originalRequest); //recall Api with new token
                } catch (error) {
                    ErrorToast(error);
                    localStorage.clear();
                    store.dispatch(actions.logout());
                    // Handle token refresh failure
                    // mostly logout the user and re-authenticate by login again
                }
            }
        }
        return Promise.reject(error);
    }
);

const responseBody = (response: AxiosResponse) => response.data;

export const requests = {
    get: (url: string, params?: any) =>
        instance
            .get(url, { params })
            .then(responseBody)
            .catch(e => {
                throw e;
            }),
    post: (url: string, body?: any) =>
        instance
            .post(url, body)
            .then(responseBody)
            .catch(e => {
                throw e;
            }),
    put: (url: string, body: any) =>
        instance
            .put(url, body)
            .then(responseBody)
            .catch(e => {
                throw e;
            }),
    delete: (url: string) =>
        instance
            .delete(url)
            .then(responseBody)
            .catch(e => {
                throw e;
            }),
};

export const downloadFileRequest = (
    url: string,
    format?: string,
    fileName?: string | null,
    params?: any,
    setIsLoading?: any
) => {
    const appVersion = localStorage.getItem('appVersion');
    const token = localStorage.getItem('token') ?? '{}';
    setIsLoading(true);
    const method = 'GET';
    console.log(url);
    axios
        .request({
            url,
            method,
            responseType: 'blob',
            headers: {
                Authorization: token ? `Bearer ${token}` : '',
                ...(appVersion ? { 'app-version': appVersion } : {}),
            },
            params: params,
        })
        .then(response => {
            fileName = response.headers['content-disposition'].slice(
                22,
                response.headers['content-disposition'].length - 1
            );
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `${fileName}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            SuccessToast('Successfully downloaded');
        })
        .catch(error => ErrorToast(error))
        .finally(() => setIsLoading(false));
};

export const downloadFileRequestLoading = (
    url: string,
    format: string,
    fileName?: string | null,
    params?: any
): Promise<{ loading: boolean }> => {
    const token = localStorage.getItem('token') ?? '{}';
    const appVersion = localStorage.getItem('appVersion');

    const method = 'GET';
    return new Promise((resolve, reject) => {
        axios
            .request({
                url,
                method,
                responseType: 'blob',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    ...(appVersion ? { 'app-version': appVersion } : {}),
                },
                params: params,
            })
            .then(response => {
                fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0];
                const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', `${fileName}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                SuccessToast('Successfully download');
                resolve({ loading: false });
            })
            .catch(error => {
                reject(error);
            });
    });
};

export function multipartFormData(image: any, url: string, body?: any, method = 'put') {
    const formData = new FormData();
    formData.append('file', image);
    formData.append('body', body);
    if (method === 'put') {
        return instance.put(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    } else {
        return instance.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
}

export function cancelWithDocument(url: string, data: { note?: string; file?: File | null }) {
    const formData = new FormData();
    if (data.note?.trim()) {
        formData.append('note', data.note.trim());
    }
    if (data.file) {
        formData.append('file', data.file);
    }
    return instance.put(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => res.data);
}
